import React from "react";
import { CalendarClock, Paperclip, Trash2 } from "lucide-react";
import { formatDateDDMMYYYY } from "../../../../shared/helpers/date";
import {
  getAppointmentStatusLabel,
  getAppointmentStatusColor,
  canDeleteAppointment,
} from "src/entities/appointment";
import { buildReschedulingLabel } from "src/entities/schedule";
import { IconButton } from "../../../../shared/ui/icons/IconButton/IconButton";
import { TechnicianFeedbackBadge } from "../../../../shared/ui/feedback/TechnicianFeedbackBadge";
import { BadgeCell } from "./BadgeCell";
import type { AppointmentListLayoutProps } from "../model/appointmentList.types";
import {
  canReschedule,
  formatActualRange,
  formatDurationTriplet,
  formatTime,
  isShowActualDuration,
  isShowActualTime,
} from "../model/appointmentRow";

/** One labelled value inside a card. Keeps the label/value pairs typographically identical. */
const CardFact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-1.5">
    <span className="shrink-0 text-gray-400">{label}</span>
    <span className="min-w-0 break-words text-gray-700">{value}</span>
  </div>
);

/**
 * The Termine of a work order as stacked cards.
 *
 * The table this replaces below 768 px carries up to ten columns; the ones that matter on a phone
 * are the when, the who and the status, so the rest become labelled lines rather than a row that
 * has to be scrolled sideways to read.
 */
export const AppointmentCardList: React.FC<AppointmentListLayoutProps> = ({
  appointments,
  onEdit,
  onReschedule,
  onDelete,
}) => (
  <ul className="flex flex-col">
    {appointments.map((a) => {
      const showActions = canReschedule(a) || canDeleteAppointment(a);

      return (
        <li
          key={a.id}
          className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 last:border-0"
        >
          {/* A button rather than a tappable row: the actions below are buttons too, and one may
              not nest inside the other. */}
          <button
            type="button"
            onClick={onEdit ? () => onEdit(a.id) : undefined}
            disabled={!onEdit}
            className="flex w-full flex-col gap-2 text-left font-['Roboto'] text-[13px] disabled:cursor-default"
          >
            <div className="flex w-full items-start justify-between gap-2">
              <span className="font-bold text-[14px] text-[#004494]">
                {a.sequenceNumber}. Termin
              </span>

              <div className="flex shrink-0 flex-col items-end gap-1">
                <BadgeCell
                  color={getAppointmentStatusColor(a.status)}
                  label={getAppointmentStatusLabel(a.status)}
                />
                {a.schedule.needsRescheduling && (
                  <BadgeCell
                    color="#ef4444"
                    label={buildReschedulingLabel(
                      a.schedule.reschedulingCause,
                      a.schedule.reschedulingReason,
                    )}
                  />
                )}
              </div>
            </div>

            <div className="font-bold text-gray-800">
              {formatDateDDMMYYYY(a.schedule.plannedWorkStartsAt)} ·{" "}
              {formatTime(a.schedule.plannedWorkStartsAt)}
            </div>

            <div className="flex w-full flex-col gap-0.5">
              <CardFact label="Techniker" value={a.technician?.name ?? "—"} />
              <CardFact label="Gewerk" value={a.workOrder?.external?.gewerkeText ?? "—"} />
              <CardFact
                // Spelled out rather than a tooltip — there is no hover to reveal one here.
                label="Soll (An/Arb/Ab)"
                value={formatDurationTriplet(
                  a.schedule.plannedTravelMinutes,
                  a.schedule.plannedWorkMinutes,
                  a.schedule.plannedDepartureMinutes,
                )}
              />
              {isShowActualDuration(a) && (
                <CardFact
                  label="Ist (An/Arb/Ab)"
                  value={formatDurationTriplet(
                    a.schedule.actualTravelMinutes,
                    a.schedule.actualWorkMinutes,
                    a.schedule.actualDepartureMinutes,
                  )}
                />
              )}
              {isShowActualTime(a) && (
                <CardFact label="Beginn / Ende (ist)" value={formatActualRange(a)} />
              )}
            </div>

            <span className="inline-flex items-center gap-2">
              {a.attachments?.length ? (
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <Paperclip className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span className="text-xs">{a.attachments.length}</span>
                </span>
              ) : null}
              <TechnicianFeedbackBadge
                tone="muted"
                hasComment={Boolean(a.technicianComment?.trim())}
                attachmentCount={a.technicianAttachments?.length ?? 0}
                title={a.technicianComment?.trim() || undefined}
              />
            </span>
          </button>

          {showActions && (
            <div className="flex items-center justify-end gap-2">
              {canReschedule(a) && (
                <IconButton
                  variant="primary"
                  icon={<CalendarClock size={16} />}
                  label="Verschieben"
                  aria-label="Auf Kalender verschieben"
                  title="Auf Kalender verschieben"
                  onClick={() => onReschedule(a)}
                />
              )}
              {canDeleteAppointment(a) && (
                <IconButton
                  icon={<Trash2 size={16} />}
                  label="Löschen"
                  aria-label="Termin löschen"
                  title="Termin löschen"
                  onClick={() => onDelete(a)}
                />
              )}
            </div>
          )}
        </li>
      );
    })}
  </ul>
);
